import React, { useState } from 'react';
import { X, Search, Loader2, AlertCircle, User, CheckCircle, ShieldAlert } from 'lucide-react';
import { ConsultarProprietarioResponse, ConsultationProductType } from '../types';
import { consultaProprietario } from '../services/consultationService';

interface ConsultaProprietarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ConsultationProductType;
  userCredits: number;
  onSuccess: (result: ConsultarProprietarioResponse) => void;
  onInsufficientCredits: () => void;
}

const ConsultaProprietarioModal: React.FC<ConsultaProprietarioModalProps> = ({
  isOpen,
  onClose,
  product,
  userCredits,
  onSuccess,
  onInsufficientCredits,
}) => {
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatPlate = (value: string): string => {
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    if (clean.length <= 3) {
      return clean;
    } else if (clean.length <= 7) {
      return clean.slice(0, 3) + '-' + clean.slice(3);
    }
    return clean.slice(0, 3) + '-' + clean.slice(3, 7);
  };

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPlate(e.target.value);
    if (formatted.length <= 8) {
      setPlate(formatted);
      setError('');
    }
  };

  const validatePlate = (plate: string): boolean => {
    const clean = plate.replace(/[^A-Z0-9]/g, '');
    return /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(clean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (userCredits < product.creditCost) {
      onInsufficientCredits();
      return;
    }

    if (!validatePlate(plate)) {
      setError('Formato de placa inválido. Use ABC1234 ou ABC1D23.');
      return;
    }

    setLoading(true);

    try {
      const result = await consultaProprietario(plate);
      onSuccess(result);
      setPlate('');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Erro ao consultar proprietário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPlate('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl border border-slate-700/50 shadow-2xl overflow-hidden">
        {/* Gradient top border - indigo for proprietario/person */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" />
        
        {/* Header */}
        <div className="p-6 border-b border-slate-800/50">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-indigo-500/20 to-violet-500/20 rounded-2xl border border-indigo-500/30">
                <User className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{product.name}</h2>
                <p className="text-sm text-slate-400 mt-1">
                  {product.description}
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mx-6 mt-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-200">
              <p className="font-medium mb-1">Informação Parcial</p>
              <p className="text-indigo-300/80">
                Esta consulta retorna apenas o primeiro nome e o documento parcialmente mascarado. 
                Não permite identificação completa de pessoas físicas.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Plate Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Search className="w-4 h-4 text-indigo-400" />
              Placa do Veículo
            </label>
            <div className="relative">
              <input
                type="text"
                value={plate}
                onChange={handlePlateChange}
                placeholder="ABC-1D23"
                className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl py-4 px-6 text-white text-2xl font-mono tracking-[0.3em] uppercase text-center focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-600 placeholder:tracking-[0.3em]"
                disabled={loading}
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500 text-center">
              Formato aceito: ABC1234 (antigo) ou ABC1D23 (Mercosul)
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 text-red-400 text-sm bg-red-500/10 p-4 rounded-xl border border-red-500/20">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Features */}
          <div className="p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">
              Esta consulta inclui:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {product.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                  <CheckCircle className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Cost Info */}
          <div className="flex items-center justify-between p-4 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
            <div>
              <p className="text-sm text-indigo-300">Custo da consulta</p>
              <p className="text-2xl font-bold text-indigo-400">
                {product.creditCost} crédito{product.creditCost > 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Seu saldo</p>
              <p className="text-xl font-bold text-white">{userCredits} créditos</p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || plate.length < 7}
            className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-3 text-lg shadow-lg shadow-indigo-500/20 disabled:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Consultando...
              </>
            ) : (
              <>
                <User className="w-6 h-6" />
                Consultar Proprietário
              </>
            )}
          </button>

          {userCredits < product.creditCost && (
            <p className="text-center text-sm text-red-400">
              Você não tem créditos suficientes para esta consulta.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ConsultaProprietarioModal;

